import React from 'react';
import { Card, CardContent } from './ui/card';
import { FileText, Users, CheckCircle, ArrowRight } from 'lucide-react';

export const HowItWorksSection = ({ onPostProjectClick }) => {
  const steps = [
    {
      icon: FileText,
      title: "Post Your Project",
      description: "Describe your home repair or renovation needs with details, photos, and timeline. Our smart matching system will categorize your project.",
      color: "bg-blue-500"
    },
    {
      icon: Users,
      title: "Receive Competitive Bids",
      description: "Qualified service providers review your project and submit competitive bids with their pricing, timeline, and approach.",
      color: "bg-emerald-500"
    },
    {
      icon: CheckCircle,
      title: "Choose & Get Work Done",
      description: "Compare bids, review provider profiles and ratings, then select the best offer. Get your project completed with peace of mind.",
      color: "bg-teal-500"
    }
  ];

  return (
    <section id="how-it-works" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <span>Simple Process</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            How FixDirectly Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our revolutionary bidding system ensures you get the best value for your home improvement projects. 
            Here's how it works in three simple steps:
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <Card className="h-full hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-white border-4 border-gray-100 rounded-full w-8 h-8 flex items-center justify-center font-bold text-gray-700">
                      {index + 1}
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className={`${step.color} w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 transform rotate-3 hover:rotate-0 transition-transform duration-300`}>
                    <step.icon className="text-white" size={32} />
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
              
              {/* Arrow between steps */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="text-emerald-500" size={24} />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-gray-600 mb-6">
              Join thousands of homeowners who have saved money and time using our bidding platform.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  if (onPostProjectClick) {
                    onPostProjectClick();
                  } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
              >
                Post Your First Project
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};